import React, { useState, useEffect } from 'react';
import { CheckCircle, Clock, Users, Award, ArrowRight, Activity, Heart, Zap, TrendingUp, Shield, Star, Phone, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';

const Specialty = () => {
  const [activeSpecialty, setActiveSpecialty] = useState(0);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const specialties = [
    {
      id: 0,
      title: 'Joint Replacement Surgery',
      icon: '🦴',
      gradient: 'from-blue-500 to-cyan-500',
      color: 'blue',
      description: 'Complete joint replacement solutions using the latest prosthetic technology and minimally invasive techniques.',
      procedures: [
        'Total Hip Replacement',
        'Total Knee Replacement',
        'Partial Knee Replacement',
        'Shoulder Replacement',
        'Revision Joint Surgery',
        'Computer-Assisted Surgery'
      ],
      benefits: [
        'Pain Relief',
        'Improved Mobility',
        'Better Quality of Life',
        'Long-lasting Results'
      ],
      recovery: '6-12 weeks',
      successRate: '98%',
      patients: '2,500+'
    },
    {
      id: 1,
      title: 'Sports Injury Treatment',
      icon: '⚽',
      gradient: 'from-green-500 to-emerald-500',
      color: 'green',
      description: 'Specialized care for athletes and sports enthusiasts with advanced treatment protocols for quick recovery.',
      procedures: [
        'ACL Reconstruction',
        'Meniscus Repair',
        'Rotator Cuff Surgery',
        'Tennis Elbow Treatment',
        'Ankle Ligament Repair',
        'Cartilage Restoration'
      ],
      benefits: [
        'Faster Recovery',
        'Return to Sports',
        'Injury Prevention',
        'Performance Enhancement'
      ],
      recovery: '3-6 months',
      successRate: '95%',
      patients: '1,800+'
    },
    {
      id: 2,
      title: 'Physiotherapy & Rehabilitation',
      icon: '🏋️‍♂️',
      gradient: 'from-purple-500 to-pink-500',
      color: 'purple',
      description: 'Comprehensive rehabilitation programs designed to restore function and prevent future injuries.',
      procedures: [
        'Post-Surgery Rehabilitation',
        'Manual Therapy',
        'Exercise Therapy',
        'Pain Management',
        'Gait Training',
        'Strength Conditioning'
      ],
      benefits: [
        'Pain Reduction',
        'Improved Function',
        'Faster Healing',
        'Injury Prevention'
      ],
      recovery: '4-12 weeks',
      successRate: '92%',
      patients: '3,200+'
    },
    {
      id: 3,
      title: 'Trauma & Emergency Care',
      icon: '🚑',
      gradient: 'from-red-500 to-orange-500',
      color: 'red',
      description: '24/7 emergency orthopedic care for fractures, dislocations, and traumatic injuries.',
      procedures: [
        'Fracture Fixation',
        'Emergency Surgery',
        'Dislocation Reduction',
        'Wound Management',
        'Multiple Trauma Care',
        'Crush Injury Treatment'
      ],
      benefits: [
        'Immediate Care',
        'Expert Treatment',
        'Comprehensive Support',
        'Quick Recovery'
      ],
      recovery: '2-16 weeks',
      successRate: '96%',
      patients: '4,500+'
    },
    {
      id: 4,
      title: 'Spine Surgery',
      icon: '🏥',
      gradient: 'from-indigo-500 to-purple-500',
      color: 'indigo',
      description: 'Advanced spinal treatments including minimally invasive procedures for various spine conditions.',
      procedures: [
        'Disc Surgery',
        'Spinal Fusion',
        'Laminectomy',
        'Scoliosis Correction',
        'Vertebroplasty',
        'Spinal Decompression'
      ],
      benefits: [
        'Pain Relief',
        'Improved Posture',
        'Better Mobility',
        'Nerve Protection'
      ],
      recovery: '6-12 weeks',
      successRate: '94%',
      patients: '1,200+'
    },
    {
      id: 5,
      title: 'Arthroscopic Surgery',
      icon: '🔬',
      gradient: 'from-teal-500 to-cyan-500',
      color: 'teal',
      description: 'Minimally invasive joint surgeries with smaller incisions and faster recovery times.',
      procedures: [
        'Knee Arthroscopy',
        'Shoulder Arthroscopy',
        'Hip Arthroscopy',
        'Ankle Arthroscopy',
        'Cartilage Repair',
        'Ligament Reconstruction'
      ],
      benefits: [
        'Minimal Scarring',
        'Less Pain',
        'Faster Recovery',
        'Reduced Complications'
      ],
      recovery: '2-8 weeks',
      successRate: '97%',
      patients: '2,100+'
    }
  ];

  return (
    <div className="overflow-x-hidden">
      {/* Hero Section - Premium Gradient */}
      <section className="relative py-16 md:py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-10 w-72 h-72 bg-blue-500/30 rounded-full blur-3xl" />
          <div className="absolute top-10 right-0 w-80 h-80 bg-purple-500/30 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-1/3 w-96 h-96 bg-indigo-500/25 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/10 border border-blue-400/40 text-sm font-medium mb-4 backdrop-blur">
                <Activity className="w-4 h-4 mr-2 text-blue-200" />
                Comprehensive Orthopedic Super-Specialties
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
                Advanced
                <span className="block bg-gradient-to-r from-blue-200 via-teal-200 to-purple-200 bg-clip-text text-transparent">
                  Medical Specialties
                </span>
              </h1>
              <p className="text-lg md:text-xl text-blue-100/90 max-w-xl mb-8 leading-relaxed">
                From complex joint reconstruction to sports injuries and spine care, every specialty at Sharma Ortho Center follows evidence‑based protocols and modern technology.
              </p>
              <div className="grid grid-cols-3 gap-4 max-w-lg">
                <div className="bg-white/10 rounded-2xl px-4 py-3 text-center">
                  <div className="flex justify-center mb-1">
                    <Users className="w-5 h-5 text-blue-100" />
                  </div>
                  <p className="text-xl font-bold">25,000+</p>
                  <p className="text-xs text-blue-100">Patients Treated</p>
                </div>
                <div className="bg-white/10 rounded-2xl px-4 py-3 text-center">
                  <div className="flex justify-center mb-1">
                    <Star className="w-5 h-5 text-yellow-300" />
                  </div>
                  <p className="text-xl font-bold">98%</p>
                  <p className="text-xs text-blue-100">Surgical Success</p>
                </div>
                <div className="bg-white/10 rounded-2xl px-4 py-3 text-center">
                  <div className="flex justify-center mb-1">
                    <Clock className="w-5 h-5 text-emerald-200" />
                  </div>
                  <p className="text-xl font-bold">15+ yrs</p>
                  <p className="text-xs text-blue-100">Orthopedic Expertise</p>
                </div>
              </div>
            </div>

            <div className="bg-white/5 rounded-3xl border border-blue-400/20 shadow-2xl p-6 md:p-8 backdrop-blur">
              <h3 className="text-lg font-semibold mb-4 flex items-center">
                <Zap className="w-5 h-5 mr-2 text-emerald-300" />
                Quick glance at our key specialties
              </h3>
              <div className="grid grid-cols-2 gap-4">
                {specialties.slice(0, 4).map((spec) => (
                  <button
                    key={spec.id}
                    onClick={() => setActiveSpecialty(spec.id)}
                    className={`text-left rounded-2xl px-4 py-3 border text-sm font-medium transition-all ${
                      activeSpecialty === spec.id
                        ? 'bg-white text-blue-900 border-blue-400 shadow-lg'
                        : 'bg-white/5 text-blue-100 border-white/10 hover:bg-white/10'
                    }`}
                  >
                    <span className="text-lg mr-2">{spec.icon}</span>
                    {spec.title}
                  </button>
                ))}
              </div>
              <p className="mt-5 text-xs text-blue-100/80">
                Select a specialty to explore detailed procedures, benefits, and recovery timelines below.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Specialties Layout */}
      <section className="py-16 bg-white relative">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50 to-white pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Specialty chips row */}
          <div className="flex gap-3 overflow-x-auto pb-4 mb-8 -mx-4 px-4 sm:mx-0 sm:px-0">
            {specialties.map((spec) => (
              <button
                key={spec.id}
                onClick={() => setActiveSpecialty(spec.id)}
                className={`flex items-center gap-3 px-4 py-2 rounded-2xl border text-sm font-medium whitespace-nowrap transition-all ${
                  activeSpecialty === spec.id
                    ? 'bg-blue-600 text-white border-blue-600 shadow-lg shadow-blue-500/30'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                }`}
              >
                <span className="text-lg">{spec.icon}</span>
                <span>{spec.title}</span>
              </button>
            ))}
          </div>

          {/* Active specialty detail panel */}
          {(() => {
            const spec = specialties[activeSpecialty] || specialties[0];
            return (
              <div className="grid lg:grid-cols-3 gap-10 items-start">
                {/* Left: overview card */}
                <div className="lg:col-span-1">
                  <div className="relative rounded-3xl bg-white shadow-xl border border-gray-100 p-6 md:p-7">
                    <div className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold mb-4">
                      <Activity className="w-4 h-4 mr-1" />
                      Orthopedic Specialty
                    </div>
                    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2 flex items-center">
                      <span className="mr-2 text-3xl">{spec.icon}</span>
                      {spec.title}
                    </h2>
                    <p className="text-gray-600 mb-6 leading-relaxed">
                      {spec.description}
                    </p>

                    <div className="grid grid-cols-3 gap-3 text-center text-sm">
                      <div className="bg-blue-50 rounded-2xl px-2 py-3">
                        <Clock className="w-4 h-4 mx-auto text-blue-600 mb-1" />
                        <p className="font-semibold text-gray-900">{spec.recovery}</p>
                        <p className="text-[11px] text-gray-500">Typical recovery</p>
                      </div>
                      <div className="bg-emerald-50 rounded-2xl px-2 py-3">
                        <Star className="w-4 h-4 mx-auto text-emerald-600 mb-1" />
                        <p className="font-semibold text-gray-900">{spec.successRate}</p>
                        <p className="text-[11px] text-gray-500">Success rate</p>
                      </div>
                      <div className="bg-purple-50 rounded-2xl px-2 py-3">
                        <Users className="w-4 h-4 mx-auto text-purple-600 mb-1" />
                        <p className="font-semibold text-gray-900">{spec.patients}</p>
                        <p className="text-[11px] text-gray-500">Patients treated</p>
                      </div>
                    </div>

                    <div className="mt-6 flex flex-col gap-3">
                      <Link
                        to="/contact"
                        className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-semibold shadow-md hover:bg-blue-700 transition-colors"
                      >
                        <Calendar className="w-4 h-4 mr-2" />
                        Book consultation for {spec.title.split(' ')[0]}
                      </Link>
                      <a
                        href="tel:+917727048951"
                        className="inline-flex items-center justify-center px-4 py-2.5 rounded-xl border border-blue-200 text-blue-700 text-sm font-semibold bg-blue-50 hover:bg-blue-100 transition-colors"
                      >
                        <Phone className="w-4 h-4 mr-2" />
                        24/7 Emergency Orthopedic Helpline
                      </a>
                    </div>
                  </div>
                </div>

                {/* Right: procedures and benefits */}
                <div className="lg:col-span-2 space-y-8">
                  <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 md:p-7">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Heart className="w-5 h-5 mr-2 text-red-500" />
                      Key Procedures We Offer
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {spec.procedures.map((procedure, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-2 rounded-2xl bg-blue-50/60 border border-blue-100 px-3 py-2.5"
                        >
                          <CheckCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-800">{procedure}</p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-white rounded-3xl shadow-lg border border-gray-100 p-6 md:p-7">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <Shield className="w-5 h-5 mr-2 text-emerald-600" />
                      Benefits & Outcomes
                    </h3>
                    <div className="grid sm:grid-cols-2 gap-3">
                      {spec.benefits.map((benefit, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-2 rounded-2xl bg-emerald-50/70 border border-emerald-100 px-3 py-2.5"
                        >
                          <TrendingUp className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                          <p className="text-sm text-gray-800">{benefit}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}
        </div>
      </section>

      {/* CTA Section - Premium */}
      <section className="relative py-20 bg-gradient-to-br from-blue-700 via-blue-600 to-indigo-700 text-white overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{
          backgroundImage: `radial-gradient(circle at 20% 20%, rgba(255,255,255,0.25), transparent 45%),
                            radial-gradient(circle at 80% 0%, rgba(248,250,252,0.2), transparent 40%)`
        }} />
        <div className="absolute inset-0 pointer-events-none" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)`,
          backgroundSize: '140px 140px'
        }} />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center px-4 py-1.5 rounded-full bg-white/15 border border-white/20 text-sm font-semibold backdrop-blur">
                <Shield className="w-4 h-4 mr-2" />
                Need Expert Orthopedic Care?
              </div>
              <h2 className="text-3xl md:text-4xl font-bold leading-snug">
                Schedule a consultation with our specialists today.
              </h2>
              <p className="text-lg text-blue-100">
                Same-day appointments, emergency trauma response, and personalized rehabilitation plans under one roof.
              </p>
              <div className="grid sm:grid-cols-2 gap-4 text-sm font-semibold">
                <div className="rounded-2xl bg-white/10 border border-white/15 px-4 py-3">
                  <p className="text-blue-100 uppercase tracking-wider text-xs">Availability</p>
                  <p className="text-white text-lg mt-1">Mon - Sun • 8 AM - 8 PM</p>
                </div>
                <div className="rounded-2xl bg-white/10 border border-white/15 px-4 py-3">
                  <p className="text-blue-100 uppercase tracking-wider text-xs">Emergency Command</p>
                  <p className="text-white text-lg mt-1">24/7 Trauma Support</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl bg-white text-blue-700 font-semibold shadow-lg hover:bg-slate-100 transition-colors"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Book Appointment
                </Link>
                <a
                  href="tel:+917727048951"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl border border-white/60 bg-white/10 text-white font-semibold hover:bg-white/20 transition-colors shadow-md"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call: +91 77270 48951
                </a>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div className="bg-white/10 border border-white/15 rounded-3xl p-5 shadow-xl">
                <p className="text-sm uppercase tracking-wider text-blue-100">Why book with us</p>
                <ul className="mt-4 space-y-3 text-blue-50 text-sm">
                  {['Advanced diagnostic imaging', 'Minimally invasive surgical options', 'Insurance assistance & documentation'].map(item => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-300 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white/10 border border-white/15 rounded-3xl p-5 shadow-xl">
                <p className="text-sm uppercase tracking-wider text-blue-100">What you'll receive</p>
                <ul className="mt-4 space-y-3 text-blue-50 text-sm">
                  {['Personalized treatment roadmap', 'Direct consultant follow-up', 'Rehabilitation starter kit'].map(item => (
                    <li key={item} className="flex items-start gap-2">
                      <CheckCircle className="w-4 h-4 text-emerald-300 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Specialty;
